import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  MedicalServices as MedicalServicesIcon,
  People as PeopleIcon,
  LocationOn as LocationOnIcon,
  Article as ArticleIcon,
  Diamond as DiamondIcon,
  LocalHospital as LocalHospitalIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarTodayIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  HelpOutline as HelpOutlineIcon,
  AttachMoney as AttachMoneyIcon,
  Work as WorkIcon,
  Mail as MailIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import './AdminLayout.scss';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const closeMobileMenu = () => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: DashboardIcon },
    { path: '/admin/home-content', label: 'Home Content', icon: HomeIcon },
    { path: '/admin/services', label: 'Services', icon: MedicalServicesIcon },
    { path: '/admin/team', label: 'Team Members', icon: PeopleIcon },
    { path: '/admin/locations', label: 'Locations', icon: LocationOnIcon },
    { path: '/admin/blog', label: 'Blog Posts', icon: ArticleIcon },
    { path: '/admin/core-values', label: 'Core Values', icon: DiamondIcon },
    { path: '/admin/insurance', label: 'Insurance', icon: LocalHospitalIcon },
    { path: '/admin/conditions', label: 'Conditions', icon: AssignmentIcon },
    { path: '/admin/faq', label: 'FAQ', icon: HelpOutlineIcon },
    { path: '/admin/fees', label: 'Fees', icon: AttachMoneyIcon },
    { path: '/admin/job-listings', label: 'Job Listings', icon: WorkIcon },
    { path: '/admin/work-with-us', label: 'Work With Us', icon: GroupsIcon },
    { path: '/admin/contact-inquiries', label: 'Contact Inquiries', icon: MailIcon },

    { path: '/admin/settings', label: 'Settings', icon: SettingsIcon },
  ];

  return (
    <div className="admin-layout">
      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div className="mobile-overlay" onClick={closeMobileMenu}></div>
      )}

      {/* Sidebar */}
      <div
        className={`sidebar ${sidebarOpen ? 'open' : 'closed'} ${
          isMobile && mobileMenuOpen ? 'mobile-open' : ''
        }`}
      >
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
          {!isMobile && (
            <button className="toggle-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
              {sidebarOpen ? '◀' : '▶'}
            </button>
          )}
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <IconComponent className="nav-icon" />
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <LogoutIcon className="nav-icon" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-content">
        <header className="admin-header">
          {isMobile && (
            <button className="mobile-menu-btn" onClick={toggleSidebar} aria-label="Open menu">
              <MenuIcon />
            </button>
          )}
          <h1>Admin Panel</h1>
        </header>
        <main className="admin-main">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
